import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { API_ORDER_ERROR_HANDLINGS_CRM, API_SITES_USERVISIT, API_USERS_CRM, BACKEND_HOST, getDataFromUrl } from '../../api';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import Widgets from '../../Components/Common/Widgets';
import CommonDatePicker from "@/Components/Common/CommonDatePicker";
import { read, utils, writeFileXLSX, writeFile } from 'xlsx';
import Select from "react-select";
import { useNavigate } from 'react-router-dom';
import BalanceOverview from '../Statistics/SalesStatistics/BalanceOverview';
import { mergeSiteStatistics, upsertStatisticByDate } from '../Statistics/SalesStatistics/statisticsUtils';
import DashboardChangelogCard from './DashboardChangelogCard';
import ChangelogModal from './ChangelogModal';
import { dismissChangelog, fetchDashboardChangelog, shouldShowChangelog, type DashboardChangelog } from './changelog';
import BillingStatusCard, { BillingManagementCard } from './BillingStatusCard';
import { useAuthContext } from '../../Components/AuthContext';
import { isSitePaused } from '../../common/permissions';
import DashboardQuickMenu from './DashboardQuickMenu';


function weekCount(year, month_number, day_num = 0) {
    // month_number is in the range 0..11
    const firstOfMonth = moment().year(year).month(month_number)
    const lastOfMonth = moment().year(year).endOf('month')
    let lastday = lastOfMonth.date()
    if (day_num > 0) lastday = day_num
    const used = firstOfMonth.day() + lastday;
    return Math.ceil(used / 7);
}

function export_receipt_data(data) {
    return data
        // .sort((a, b) => (moment(a["사용일"]) > moment(b["사용일"]) ? 1 : -1))
        .map(item => {
            return {
                "order_id": item["order_id"],
                "분류": item["type"],
                "결재일": moment(item["order_date"]).format('YYYY-MM-DD HH:mm:ss'),
                "취소일": item["cancel_date"] ? moment(item["cancel_date"]).format('YYYY-MM-DD') : "",
                "고객": item["username"] + "(" + item["phone_num"] + ")",
                // "phone_num": item["phone_num"],
                "상품명": item["name"],
                "상세정보": item["info"],
                "key": item["payments_key"].slice(0, 5) + "..",
                // "주문총금액": item["order_amount"],
                // "old": item["_amount"],
                "금액": item["amount"],
            }
        })
}

const DashboardCrm = () => {
    document.title = "CRM";

    const navigate = useNavigate()
    const { userInfo } = useAuthContext();
    const [availableDashboardChangelog, setAvailableDashboardChangelog] = useState<DashboardChangelog | null>(null);
    const [isDashboardChangelogOpen, setIsDashboardChangelogOpen] = useState(false);
    const [isDashboardChangelogLoading, setIsDashboardChangelogLoading] = useState(false);

    const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'))
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'))

    const [siteStatistics, setSiteStatistics] = useState([])

    const [siteStatistic, setSiteStatistic] = useState(null);
    const [isAggregating, setIsAggregating] = useState(false);

    const [siteInfo, setSiteInfo] = useState(null);
    const [userVisitData, setUserVisitData] = useState([])
    const [loadingUserVisit, setLoadingUserVisit] = useState(false)
    const [loadingStatistic, setLoadingStatistic] = useState(false)
    const [loadingReceipt, setLoadingReceipt] = useState(false)

    useEffect(() => {
        let isMounted = true;

        setIsDashboardChangelogLoading(true);
        fetchDashboardChangelog()
            .then((changelog) => {
                if (!isMounted || !changelog) return;
                setAvailableDashboardChangelog(changelog);
                if (shouldShowChangelog(changelog.hash)) setIsDashboardChangelogOpen(true);
            })
            .catch((error) => {
                console.warn('Failed to load CRM changelog', error);
            })
            .finally(() => {
                if (isMounted) setIsDashboardChangelogLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const closeDashboardChangelog = () => {
        if (availableDashboardChangelog) dismissChangelog(availableDashboardChangelog.hash);
        setIsDashboardChangelogOpen(false);
    };

    const openDashboardChangelog = () => {
        if (!availableDashboardChangelog) return;
        setIsDashboardChangelogOpen(true);
    };

    React.useEffect(() => {
        const dataPeriod = 7;
        const weekBefore = moment().startOf('day').subtract(dataPeriod - 1, 'days');

        // 그래프용 7일 데이터
        getDataFromUrl(`${BACKEND_HOST}/sites/statistic/?start_date=${weekBefore.format('YYYY-MM-DD')}`)
            .then((data) => {
                setSiteStatistics(data)
            }).catch((e) => {
                console.warn(e)
            })

        // 오늘 데이터 (즉시 반환 + 백그라운드 갱신 트리거)
        getDataFromUrl(`${BACKEND_HOST}/sites/statistic/today/`)
            .then((data) => {
                if (!data) return;
                setSiteStatistic(data)
                setSiteStatistics((prev) => upsertStatisticByDate(prev, data))
                const isToday = moment(data.date).isSame(moment(), 'day')
                setIsAggregating(!isToday)
            }).catch((e) => {
                console.warn(e)
            })

        setLoadingUserVisit(true)
        getDataFromUrl(API_SITES_USERVISIT).then((data) => {
            console.log("user visit date:", data)
            if (data.result) {
                setUserVisitData(data.result)
                setLoadingUserVisit(false)
            }
        })
    }, [])

    // 집계 중일 때 10초마다 폴링 (최대 6회 = 1분)
    React.useEffect(() => {
        if (!isAggregating) return;

        let pollCount = 0;
        const interval = setInterval(() => {
            pollCount += 1;
            getDataFromUrl(`${BACKEND_HOST}/sites/statistic/today/`)
                .then((data) => {
                    if (!data) return;
                    const isToday = moment(data.date).isSame(moment(), 'day')
                    if (isToday) {
                        setSiteStatistic(data)
                        setSiteStatistics((prev) => upsertStatisticByDate(prev, data))
                        setIsAggregating(false)
                    }
                }).catch((e) => {
                    console.warn(e)
                })
            if (pollCount >= 6) {
                clearInterval(interval)
                setIsAggregating(false)
            }
        }, 10000)

        return () => clearInterval(interval)
    }, [isAggregating])

    useEffect(() => {
        if (siteStatistic) {
            getDataFromUrl(`${BACKEND_HOST}/sites/camps/${siteStatistic.site}`)
                .then((data) => {
                    // console.log("/sites/camps/", data);
                    // const campsite = data.find((item) => item.id === siteStatistic.site)
                    // setSiteInfo(campsite)
                    setSiteInfo(data)
                }).catch((e) => {
                    console.warn(e)
                })
        }
    }, [siteStatistic])

    const handlePickStatistic = (value) => {
        setLoadingStatistic(true)

        const start_date = moment(value[0]).format('YYYY-MM-DD')
        const end_date = moment(value[1]).format('YYYY-MM-DD')

        setStartDate(start_date)
        setEndDate(end_date)

        getDataFromUrl(`${BACKEND_HOST}/sites/statistic/?start_date=${start_date}&end_date=${end_date}`)
            .then((data) => {
                // console.log("handlePickStatistic", data)
                if (data.length) {
                    setSiteStatistic(mergeSiteStatistics(data))
                }
            }).catch((e) => {
                console.warn(e)
            }).finally(() => { setLoadingStatistic(false) })
    }

    const [termType, setTermType] = useState("day") // day, week, month
    const [selectYear, setSelectYear] = useState(moment().year())
    const [selectMonth, setSelectMonth] = useState(moment().month())
    const [selectDay, setSelectDay] = useState(moment().date())
    const [selectWeek, setSelectWeek] = useState(weekCount(moment().year(), moment().month(), moment().date()) + 1)
    const isGlobalAdmin = Boolean(userInfo?.is_global_admin);
    const hasSiteOwner = Boolean(userInfo?.capabilities?.includes("site_owner"));
    const isPausedSite = isSitePaused(userInfo);

    // const selectYearRange = [
    //     { label: "2022년", value: 2022 },
    //     { label: "2023년", value: 2023 },
    //     { label: "2024년", value: 2024 },
    // ]
    // array from 2022 to current year
    const selectYearRange = Array.from(new Array(moment().year() - 2021), (val, index) => 2022 + index)
        .map((val) => ({ label: val + "년", value: val }))

    const selectMonthRange = useMemo(() => {
        let month_num = 12
        if (selectYear === moment().year()) month_num = moment().month() + 1
        const _range = new Array(month_num).fill({}).map((_, idx) => ({ label: (idx + 1) + "월", value: idx }))
        return _range
    }, [selectYear])
    // const selectDayRange = useMemo(() => {
    //     let day_num = moment().year(selectYear).month(selectMonth).endOf("month")
    //     if (selectYear === moment().year() && selectMonth === moment().month()) day_num = moment().date() + 1
    //     const _range = new Array(day_num).fill({}).map((_, idx) => ({ label: (idx + 1) + "일", value: idx + 1 }))
    //     return _range
    // }, [selectYear, selectMonth])
    // const selectWeekRange = useMemo(() => {
    //     let week_num = weekCount(moment().year(), moment().month())
    //     if (selectYear === moment().year() && selectMonth === moment().month()) week_num = weekCount(moment().year(), moment().month(), moment().date()) 
    //     const _range = new Array(week_num).fill({}).map((_, idx) => ({ label: (idx + 1) + "번째주", value: idx + 1 }))
    //     return _range
    // }, [selectYear, selectMonth])


    const getGraphStatistics = (value) => {
        const start_date = moment(value[0])
        const end_date = moment(value[1])
        // console.log("start_date",start_date,"end_date",end_date)
        // console.log("dataPeriod",dataPeriod)
        // console.log(`?start_date=${start_date.format('YYYY-MM-DD')}&end_date=${end_date.format('YYYY-MM-DD')}`)
        getDataFromUrl(`${BACKEND_HOST}/sites/statistic/?start_date=${start_date.format('YYYY-MM-DD')}&end_date=${end_date.format('YYYY-MM-DD')}`)
            .then((data) => {
                console.log("getGraphStatistics", data)
                if (data.length) setSiteStatistics(data)
            }).catch((e) => {
                console.warn(e)
            })
    }

    // useEffect(() => {
    //     let _start_date = moment().year(selectYear).month(selectMonth).date(1)
    //     let _end_date = moment().year(selectYear).month(selectMonth).endOf('month')
    //     if (termType === "day") {
    //         _start_date = moment().year(selectYear).month(selectMonth).date(selectDay)
    //         _end_date = moment().year(selectYear).month(selectMonth).date(selectDay)
    //     } else if (termType === "week") {
    //         let target_date = moment().year(selectYear).month(selectMonth).date(_start_date.date() + ((selectWeek - 1) * 7))
    //         console.log(target_date.format('YYYY-MM-DD'))
    //         _start_date = moment().year(selectYear).month(selectMonth).date(target_date.date() - target_date.day())
    //         _end_date = moment().year(selectYear).month(selectMonth).date(target_date.date() - target_date.day() + 6)
    //         // _start_date = target_date.startOf('week')
    //         // _end_date = target_date.endOf('week')
    //     }

    //     // console.log("change date here", _start_date.format('YYYY-MM-DD'), _end_date.format('YYYY-MM-DD'))
    //     handlePickStatistic([_start_date.toDate(), _end_date.toDate()])
    // }, [termType, selectYear, selectMonth, selectDay, selectWeek])

    const [recentAdjustment, setRecentAdjustment] = useState(null)
    const [isAdjustmentLoading, setIsAdjustmentLoading] = useState(false)

    useEffect(() => {
        if (!siteStatistic) return;

        setIsAdjustmentLoading(true)
        getDataFromUrl(`${BACKEND_HOST}/sites/adjustment-receipt-list/?start_date=${siteStatistic["date"]}&end_date=${siteStatistic["date"]}`)
            .then((data) => {
                console.log("adjustment-receipt-list", data, siteStatistic["date"]);
                const _data = data.result.filter(item => {
                    if (item.use_date === moment().format('YYYY-MM-DD')) {
                        return item.state !== "예약"
                    }
                    return true
                })
                setRecentAdjustment(_data)
            })
            .finally(() => {
                setIsAdjustmentLoading(false)
            })
    }, [siteStatistic])

    const [totalUserCount, setTotalUserCount] = useState(0)
    const [paymentErrorCount, setPaymentErrorCount] = useState(0)
    const [loadingPaymentErrors, setLoadingPaymentErrors] = useState(false)

    useEffect(() => {
        getDataFromUrl(API_USERS_CRM)
            .then((data) => {
                setTotalUserCount(data.count);
            })
            .catch((e) => { console.log(e); })
    }, [])

    useEffect(() => {
        setLoadingPaymentErrors(true)
        getDataFromUrl(`${API_ORDER_ERROR_HANDLINGS_CRM}?status=open&page=1&page_size=1`)
            .then((data) => {
                setPaymentErrorCount(data.total || 0)
            })
            .catch((e) => {
                console.warn(e)
            })
            .finally(() => {
                setLoadingPaymentErrors(false)
            })
    }, [])

    const todayUserData = userVisitData.find(item => item.date === siteStatistic?.date)

    const dayTitle = useMemo(() => {
        if (!siteStatistic) return;

        if (moment(siteStatistic["date"]).isSame(moment(), "day")) return "오늘"
        return moment(siteStatistic["date"]).format("M월 D일")
    }, [siteStatistic])

    return (
        <React.Fragment>
            <ChangelogModal
                isOpen={isDashboardChangelogOpen && Boolean(availableDashboardChangelog)}
                blocks={availableDashboardChangelog?.blocks ?? []}
                onDismiss={closeDashboardChangelog}
            />
            <div className="page-content">
                <div className="container-fluid">
                    <BreadCrumb title="CRM" pageTitle="Dashboards" />

                    <DashboardQuickMenu isPausedSite={isPausedSite} />

                    <div className="flex flex-wrap -mx-3" style={{ marginBottom: "20px", justifyContent: "flex-end" }}>

                        <div style={{ width: "220px", display: "flex", flexDirection: "row", alignItems: "center" }}>
                            <div style={{ width: "48px" }}><span className="text-muted fs-15">날짜: </span></div>
                            {termType !== "day" ?
                                <div className="d-flex flex-row gap-2">
                                    <Select
                                        className="mb-0 fs-6"
                                        defaultValue={selectYearRange.find((val) => (selectYear === val.value))}
                                        onChange={(val) => setSelectYear(val.value)}
                                        options={selectYearRange}
                                        id={"choices-single-year-range"}
                                    ></Select>
                                    <Select
                                        className="mb-0 fs-6"
                                        defaultValue={selectMonthRange.find((val) => (selectMonth === val.value))}
                                        onChange={(val) => setSelectMonth(val.value)}
                                        options={selectMonthRange}
                                        id={"choices-single-month-range"}
                                    ></Select>
                                </div> :
                                <CommonDatePicker
                                    // id="date-field"
                                    className="form-control"
                                    size={"32px"}
                                    options={{
                                    }}
                                    placeholder="Select Date"
                                    value={siteStatistic?.date || moment().format('YYYY-MM-DD')}
                                    onChange={(value) => {
                                        handlePickStatistic([value[0], value[0]]);
                                    }}
                                />
                            }
                        </div>
                    </div>

                    <div className="flex flex-wrap -mx-3">

                        <div className="w-full xl:w-4/12 md:w-6/12 sm:w-full px-3">
                            <BoardCard onClick={() => navigate("/sales-statistics")}>
                                <div style={{ fontSize: "18px", fontWeight: "600", color: "#4B465C", width: "100%", display: "flex", alignItems: "center", gap: "8px" }}>
                                    {dayTitle}의 판매
                                    {isAggregating && (
                                        <span style={{ fontSize: "12px", color: "#888", fontWeight: "400", display: "flex", alignItems: "center", gap: "4px" }}>
                                            <span className="spinner-border spinner-border-sm" role="status" style={{ width: "12px", height: "12px" }} />
                                            집계 중...
                                        </span>
                                    )}
                                </div>
                                {loadingStatistic ? <div className='fs-6'>
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <span>데이터를 로딩하는 중입니다...</span>
                                </div> : siteStatistic ?
                                    <div style={{ width: "100%" }}>
                                        <div className="d-flex flex-row gap-2"
                                            style={{ fontSize: "25px", fontWeight: "600", color: "#4B465C" }}>
                                            <div className=''>합계 <span>{new Intl.NumberFormat().format(siteStatistic["total_amount"])}</span></div>
                                        </div>
                                        <div style={{ height: "20px" }} />
                                        <div className="d-flex flex-row flex-wrap gap-2" style={{ fontSize: "15px" }}>
                                            <div style={{ width: "100%", overflowX: "clip" }}>
                                                <div style={{
                                                    padding: "6px", background: "rgba(115, 103, 240, 0.08)",
                                                    borderRadius: "7px", color: "rgba(75, 70, 92, 1)",
                                                    flexDirection: "row", display: "flex", alignItems: "center",
                                                }}>
                                                    <i className="bx bx-calendar me-1" style={{ fontSize: "20px" }} />
                                                    예약:&nbsp;<span>{new Intl.NumberFormat().format(siteStatistic["total_amount_info"]["total_reserv"] - siteStatistic["total_amount_info"]["total_cancel_reserv"])}</span>
                                                </div>
                                            </div>
                                            <div style={{ width: "100%", overflowX: "clip" }}>
                                                <div style={{
                                                    padding: "6px", background: "rgba(115, 103, 240, 0.08)",
                                                    borderRadius: "7px", color: "rgba(75, 70, 92, 1)",
                                                    flexDirection: "row", display: "flex", alignItems: "center",
                                                }}>
                                                    <i className="las la-ticket-alt me-1" style={{ fontSize: "20px" }} />
                                                    티켓:&nbsp;<span>{new Intl.NumberFormat().format(siteStatistic["total_amount_info"]["total_ticket"] - siteStatistic["total_amount_info"]["total_cancel_ticket"])}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    : null
                                }
                            </BoardCard>
                        </div>

                        <div className="w-full xl:w-4/12 md:w-6/12 sm:w-full px-3">
                            <BoardCard onClick={() => navigate("/statistics-adjustment")}>
                                <div style={{ fontSize: "18px", fontWeight: "600", color: "#4B465C", width: "100%" }}>{dayTitle}의 사용완료</div>
                                {isAdjustmentLoading ? <div className='fs-6'>
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <span>데이터를 로딩하는 중입니다...</span>
                                </div> : recentAdjustment ?
                                    <div style={{ width: "100%" }}>
                                        <div className="d-flex flex-row gap-2"
                                            style={{ fontSize: "25px", fontWeight: "600", color: "#4B465C" }}>
                                            <div className=''>합계 <span>{new Intl.NumberFormat().format(recentAdjustment.reduce((acc, cur) => {
                                                return acc + cur.amount
                                            }, 0))}</span></div>
                                        </div>
                                        <div style={{ height: "20px" }} />
                                        <div className="d-flex flex-row flex-wrap gap-2" style={{ fontSize: "15px" }}>
                                            <div style={{ width: "100%", overflowX: "clip" }}>
                                                <div style={{
                                                    padding: "6px", background: "rgba(40, 199, 111, 0.08)",
                                                    borderRadius: "7px", color: "rgba(75, 70, 92, 1)",
                                                    flexDirection: "row", display: "flex", alignItems: "center",
                                                }}>
                                                    <i className="bx bx-calendar me-1" style={{ fontSize: "20px" }} />
                                                    예약:&nbsp;<span>{new Intl.NumberFormat().format(
                                                        recentAdjustment.filter((val) => (val.type === "예약")).reduce((acc, cur) => {
                                                            return acc + cur.amount
                                                        }, 0)
                                                    )}</span>
                                                </div>
                                            </div>
                                            <div style={{ width: "100%", overflowX: "clip" }}>
                                                <div style={{
                                                    padding: "6px", background: "rgba(40, 199, 111, 0.08)",
                                                    borderRadius: "7px", color: "rgba(75, 70, 92, 1)",
                                                    flexDirection: "row", display: "flex", alignItems: "center",
                                                }}>
                                                    <i className="las la-ticket-alt me-1" style={{ fontSize: "20px" }} />
                                                    티켓:&nbsp;<span>{new Intl.NumberFormat().format(
                                                        recentAdjustment.filter((val) => (val.type === "티켓")).reduce((acc, cur) => {
                                                            return acc + cur.amount
                                                        }, 0)
                                                    )}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    : null
                                }
                            </BoardCard>
                        </div>

                        <div className="w-full xl:w-4/12 md:w-6/12 sm:w-full px-3">
                            <BoardCard onClick={() => { }}>
                                <div style={{ fontSize: "18px", fontWeight: "600", color: "#4B465C", width: "100%" }}>{dayTitle}의 방문</div>
                                {loadingUserVisit ? <div className='fs-6'>
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <span>데이터를 로딩하는 중입니다...</span>
                                </div> : todayUserData ?
                                    <div style={{ width: "100%" }}>
                                        <div className="d-flex flex-row gap-2"
                                            style={{ fontSize: "25px", fontWeight: "600", color: "#4B465C" }}>
                                            <div className=''>회원 수 <span>{new Intl.NumberFormat().format(totalUserCount)}명</span></div>
                                        </div>
                                        <div style={{ height: "20px" }} />
                                        <div className="d-flex flex-row flex-wrap gap-2" style={{ fontSize: "15px" }}>
                                            <div style={{ width: "100%", overflowX: "clip" }}>
                                                <div style={{
                                                    padding: "6px", background: "rgba(0, 207, 232, 0.08)",
                                                    borderRadius: "7px", color: "rgba(75, 70, 92, 1)",
                                                    flexDirection: "row", display: "flex", alignItems: "center",
                                                }}>
                                                    <i className="bx bx-user me-1" style={{ fontSize: "20px" }} />
                                                    방문:&nbsp;<span>{new Intl.NumberFormat().format(todayUserData.visiter_count.length)}</span>
                                                </div>
                                            </div>
                                            <div style={{ width: "100%", overflowX: "clip" }}>
                                                <div style={{
                                                    padding: "6px", background: "rgba(0, 207, 232, 0.08)",
                                                    borderRadius: "7px", color: "rgba(75, 70, 92, 1)",
                                                    flexDirection: "row", display: "flex", alignItems: "center",
                                                }}>
                                                    <i className="las la-sign-in-alt me-1" style={{ fontSize: "22px" }} />
                                                    가입:&nbsp;<span>{new Intl.NumberFormat().format(todayUserData.register_count.length)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    : null
                                }
                            </BoardCard>
                        </div>

                    </div>

                    <div className="flex flex-wrap -mx-3">

                        <div className="w-full xl:w-8/12 md:w-6/12 sm:w-full px-3">
                            {siteStatistics.length ?
                                <BalanceOverview title={`일별 매출 현황 차트`}
                                    start_date={siteStatistics[0]["date"]}
                                    end_date={siteStatistics[siteStatistics.length - 1]["date"]}
                                    onChange={(value) => {
                                        // ${siteStatistics[0]["date"]} - ${siteStatistics[siteStatistics.length - 1]["date"]}
                                        // console.log("graph date change:", value)
                                        getGraphStatistics(value)
                                    }}
                                    dataList={siteStatistics} /> : null}
                        </div>

                        <div className="w-full xl:w-4/12 md:w-6/12 sm:w-full px-3">
                            <div className="card card-height-100">
                                <div className="card-header align-items-center d-flex">
                                    <h4 className="card-title mb-0 flex-grow-1">방문자 통계</h4>
                                </div>
                                <div className="card-body px-2" style={{ padding: "0" }}>
                                    {(!loadingUserVisit) ? <table id="user-visit-table" className="table custom-header-css">
                                        <thead className="table-light text-muted">
                                            <tr>
                                                <th>날짜</th>
                                                <th>방문자 수</th>
                                                <th>가입자 수</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {userVisitData.map((value, idx) => {
                                                return <tr key={idx}>
                                                    <td>{value.date}</td>
                                                    <td>{value.visiter_count.length}</td>
                                                    <td>{value.register_count.length}</td>
                                                </tr>
                                            })}
                                        </tbody>
                                    </table> : <div style={{}}>
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <span>데이터를 로딩하는 중입니다...</span>
                                    </div>}
                                </div>
                            </div>
                        </div>

                    </div>

                    <div className="flex flex-wrap -mx-3">

                        <div className="w-full xl:w-4/12 md:w-6/12 sm:w-full px-3">
                            <BoardCard onClick={() => navigate("/order-error-handlings")}>
                                <div style={{ fontSize: "18px", fontWeight: "600", color: "#4B465C", width: "100%" }}>결제 오류 주문</div>
                                {loadingPaymentErrors ? <div className='fs-6'>
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <span>데이터를 로딩하는 중입니다...</span>
                                </div> : <div style={{ width: "100%" }}>
                                    <div className="d-flex flex-row gap-2"
                                        style={{ fontSize: "25px", fontWeight: "600", color: "#4B465C" }}>
                                        <div>미처리 <span>{new Intl.NumberFormat().format(paymentErrorCount)}</span>건</div>
                                    </div>
                                    <div style={{ height: "20px" }} />
                                    <div style={{
                                        padding: "6px", background: "rgba(255, 159, 67, 0.12)",
                                        borderRadius: "7px", color: "rgba(75, 70, 92, 1)",
                                        display: "flex", alignItems: "center",
                                    }}>
                                        <i className="bx bx-error-circle me-1" style={{ fontSize: "20px" }} />
                                        결제 오류 주문 목록과 CSV 내보내기
                                    </div>
                                </div>}
                            </BoardCard>
                        </div>

                        <div className="w-full xl:w-4/12 md:w-6/12 sm:w-full px-3">
                            <DashboardChangelogCard
                                hasChangelog={Boolean(availableDashboardChangelog)}
                                isLoading={isDashboardChangelogLoading}
                                onOpen={openDashboardChangelog}
                            />
                        </div>

                        <div className="w-full xl:w-4/12 md:w-6/12 sm:w-full px-3">
                            <BillingStatusCard
                                hasSiteOwner={hasSiteOwner}
                            />
                        </div>

                        {isGlobalAdmin ? (
                            <div className="w-full xl:w-4/12 md:w-6/12 sm:w-full px-3">
                                <BillingManagementCard />
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </React.Fragment >
    );
};

export default DashboardCrm;


const BoardCard = ({ children, ...props }) => {
    return (
        <div className="col-xl-12" {...props}>
            <div className="card crm-widget">
                <div className="card-body" style={{ padding: "26px" }}>
                    <div className="row row-cols-xxl-3 row-cols-md-3 row-cols-1 g-0">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};
